import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, Output, EventEmitter, Input, ChangeDetectorRef } from "@angular/core";
import { RebalanceService } from "@irb/irb-editors/rebalance/rebalance.service";
import { CalltoactionService } from "@irb/irb-common/components/calltoaction/calltoaction.service";
import { SellCoveredcallGridLayoutService } from "./sell-coveredcall.grid-layout.service";
import { ICoveredCall, ICoveredCallAllocation } from "@irb/irb-editors/rebalance/irb-rebalance.model";
import { DecimalPipe } from "@angular/common";
import { IrbClientsEvents } from "@irb/irb-clients/irb-clients.events";
import { AppService } from "@irb/app.service";
import { OptionsFormatterService } from "../../options-formatter.service";
import { IrbOptionChainsService } from "@irb/irb-option-chains/irb-option-chains.service";
import { Subscription } from "rxjs";

@Component({ standalone: false,
    selector: "sell-coveredcall-settings",
    templateUrl: "./sell-coveredcall-settings.component.html",
    styleUrls: [
        "./sell-coveredcall-settings.component.scss"
    ],
    providers: [DecimalPipe],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class SellCoveredcallSettingsComponent implements OnInit, OnDestroy {

    @Output() change = new EventEmitter<any>();

    _data: ICoveredCall;
    _isReadOnly: boolean;

    _defaultPercentOfUnderlyingToCover: number;

    _grid: any;
    _gridOptions: any;

    private optionChainSubscription: Subscription;
    frameworkComponents: any;

    constructor(private cdr: ChangeDetectorRef,
        private gls: SellCoveredcallGridLayoutService,
        public ctas: CalltoactionService,
        private as: AppService,
        private readonly rebalanceService: RebalanceService,
        public decimalPipe: DecimalPipe,
        public optionFormatter: OptionsFormatterService,
        private irbOptionChainsService: IrbOptionChainsService) {

        this.defaultPercentOfUnderlyingToCover = 100;

        this.gridOptions = {
            columnDefs: this.gls.getColumnDefs(this),            
            floatingFilter: false,
            context: { componentParent: this },
            isExternalFilterPresent: this.isExternalFilterPresent.bind(this),
            doesExternalFilterPass: this.doesExternalFilterPass.bind(this)
        };
        
        this.frameworkComponents = this.gls.getFrameworkComponents();

        this.optionChainSubscription = this.irbOptionChainsService.optionChainSymbolSelections$.subscribe((optionData) => {
            this.optionChainSelected(optionData);
        });
    }

    optionChainSelected(optionChainData: Array<string>) {
        if(optionChainData && optionChainData.length === 0) {
            this.data.allocations = [];
        } else {
            this.data.allocations = [];
            optionChainData.forEach(option => {
                this.data.allocations.push({
                    underlyingSymbol: option[0], underlyingTarget: "MODEL_PERC", optionSymbol: option[1][0],
                    percentOfUnderlyingToCover: this.defaultPercentOfUnderlyingToCover, positionType: "SHORT",
                    allocationId: null, isUIdeleted: false, isNew: true,
                    optionSymbolToRollTo: null, percentOfUnderlyingToPurchase: null,
                    percentOfUnderlyingToTrim: null, percentOfOptionToBuyBack: null, percentOfCoverCallToRoll: null,
                    optionSecId: null, optionSelectionMode: null, daysToExpiration: null
                } as ICoveredCallAllocation);
            });
        }
       
        this.loadGrid();
        this.change.emit(true);
    }

    onGridReady(params: any) {
        this.grid = params;
        this.grid.api.onFilterChanged();
        this.load();
    }

    loadGrid() {
        this.grid.api.setGridOption('rowData', this.data.allocations);
        // setTimeout(() => {
        //     if (this.grid && this.grid.api) {
        //         this.grid.api.sizeColumnsToFit();
        //     }
        // });
    }

    ngOnInit(): void {
    }

    ngOnDestroy(): void {
        this.optionChainSubscription.unsubscribe();
    }

    load() {
        if (!this.isReadOnly) {
            this.data.allocations = [];
        }
        this.loadGrid();
    }

    applyPercentOfUnderlyingToCover() {
        if (this.data.allocations && this.data.allocations.length > 0) {
            this.data.allocations.forEach(allocation => {
                allocation.percentOfUnderlyingToCover = Number(this.defaultPercentOfUnderlyingToCover);
            });
            this.loadGrid();
            this.change.emit(true);
        }
    }

    addAllocations() {
        this.as.fireEvent(IrbClientsEvents.SHOW_OPTION_CHAINS_DIALOG, {
            data: {action: 'SELL_COVER_CALL', selectedOptions: this.getSelectedOptions()}
        });
    }

    getSelectedOptions() {
        let selectedOptions: { symbol: string; optionSymbol: string; }[] = [];
        this.data.allocations.filter(allocation => allocation.isUIdeleted === false).forEach((allocation: ICoveredCallAllocation) => {
            selectedOptions.push({symbol: allocation.underlyingSymbol, optionSymbol: allocation.optionSymbol});
        });
        return selectedOptions;
    }

    getRowCount() {
        return (this.data.allocations) ? this.data.allocations.length : 0;
    }

    deleteAll() {
        this.data.allocations = [];
        this.loadGrid();
        this.change.emit(true);
    }

    delete(allocation: any) {
        let allocations: ICoveredCallAllocation[] = [];
        for (let i = 0; i < this.data.allocations.length; i++) {
            if (this.data.allocations[i].underlyingSymbol !== allocation.underlyingSymbol) {
                allocations.push(this.data.allocations[i]);
            }
        }
        this.data.allocations = allocations;
        this.loadGrid();
        this.change.emit(true);
    }

    cancelDelete() {
        this.ctas.remove();
    }

    isExternalFilterPresent() {
        return true;
    }

    doesExternalFilterPass(node: any) {
        return !node.data.isUIdeleted;
    }

    @Input()
    get data() {
        return this._data;
    }

    set data(data: ICoveredCall) {
        this._data = data;
    }

    @Input()
    get isReadOnly() {
        return this._isReadOnly;
    }

    set isReadOnly(isReadOnly: boolean) {
        this._isReadOnly = isReadOnly;
    }

    get defaultPercentOfUnderlyingToCover() {
        return this._defaultPercentOfUnderlyingToCover;
    }

    set defaultPercentOfUnderlyingToCover(defaultPercentOfUnderlyingToCover: number) {
        this._defaultPercentOfUnderlyingToCover = defaultPercentOfUnderlyingToCover;
    }

    set grid(g: any) {
        this._grid = g;
    }

    get grid() {
        return this._grid;
    }

    set gridOptions(gridOpns: any) {
        this._gridOptions = gridOpns;
    }

    get gridOptions() {
        return this._gridOptions;
    }

}
